"use client";

import { c as _c } from "react/compiler-runtime";
import { FlipHorizontal2, FlipVertical2, RotateCcw, RotateCw } from "lucide-react";
import { useState } from "react";
import { CropEditor } from "@/components/CropEditor";
import { chipClass, inputClass } from "@/components/form";
import { ImageTool } from "@/components/ImageTool";
import { kindOf, outputName } from "@/lib/files";
import { fitRatio, rotateBy, toSourceCrop, type NormRect } from "@/lib/image/crop";
import { OUTPUT_FORMATS, outputFormatFor } from "@/lib/image/format";
import { rotatedSize, type Rotation } from "@/lib/image/job";
import type { Size } from "@/lib/image/size";
import { getTool } from "@/lib/tools";
import { cn } from "@/lib/utils";
const tool = getTool("crop-image")!;
const RATIOS = [{
  id: "free",
  label: "Free",
  ratio: null
}, {
  id: "1:1",
  label: "1:1",
  ratio: 1
}, {
  id: "4:3",
  label: "4:3",
  ratio: 4 / 3
}, {
  id: "3:4",
  label: "3:4",
  ratio: 3 / 4
}, {
  id: "16:9",
  label: "16:9",
  ratio: 16 / 9
}, {
  id: "9:16",
  label: "9:16",
  ratio: 9 / 16
}, {
  id: "custom",
  label: "Custom",
  ratio: null
}] as const;
type RatioId = (typeof RATIOS)[number]["id"];
const fileKey = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;
export function CropTool() {
  const $ = _c(38);
  const [ratioId, setRatioId] = useState("free");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      w: "3",
      h: "2"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [custom, setCustom] = useState(t0);
  const [rotate, setRotate] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [frame, setFrame] = useState(null);
  const customRatio = Number(custom.w) / Number(custom.h);
  let t1;
  if ($[1] !== customRatio || $[2] !== ratioId) {
    t1 = ratioId === "custom" ? customRatio > 0 && Number.isFinite(customRatio) ? customRatio : null : RATIOS.find(r => r.id === ratioId)?.ratio ?? null;
    $[1] = customRatio;
    $[2] = ratioId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const ratio = t1;
  let t2;
  if ($[4] !== ratio || $[5] !== ratioId || $[6] !== rotate) {
    t2 = file => `${fileKey(file)}|${rotate}|${ratioId}|${ratio}`;
    $[4] = ratio;
    $[5] = ratioId;
    $[6] = rotate;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const keyFor = t2;
  let t3;
  if ($[8] !== frame || $[9] !== keyFor || $[10] !== ratio || $[11] !== rotate) {
    t3 = function rectFor(file_0, source) {
      if (frame?.key === keyFor(file_0)) {
        return frame.rect;
      }
      return fitRatio(rotatedSize(source, rotate), ratio);
    };
    $[8] = frame;
    $[9] = keyFor;
    $[10] = ratio;
    $[11] = rotate;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const rectFor = t3;
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = function flip(axis) {
      setFrame(old => old && {
        ...old,
        rect: axis === "h" ? {
          ...old.rect,
          x: 1 - old.rect.x - old.rect.w
        } : {
          ...old.rect,
          y: 1 - old.rect.y - old.rect.h
        }
      });
      if (axis === "h") {
        setFlipH(_temp);
      } else {
        setFlipV(_temp2);
      }
    };
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const flip = t4;
  let t5;
  if ($[14] !== flipH || $[15] !== flipV || $[16] !== rectFor || $[17] !== rotate) {
    t5 = (file_1, source_0) => {
      const crop = toSourceCrop(rectFor(file_1, source_0), source_0, rotate, flipH, flipV);
      const format = outputFormatFor(kindOf(file_1), "keep");
      return {
        job: {
          crop,
          rotate,
          flipH,
          flipV,
          format
        },
        name: () => outputName(file_1.name, "cropped", OUTPUT_FORMATS[format].ext)
      };
    };
    $[14] = flipH;
    $[15] = flipV;
    $[16] = rectFor;
    $[17] = rotate;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== flipH || $[20] !== flipV || $[21] !== keyFor || $[22] !== ratio || $[23] !== rectFor || $[24] !== rotate) {
    t6 = t7 => {
      const {
        file: file_2,
        source: source_1
      } = t7;
      return <CropEditor file={file_2} source={source_1} rotate={rotate} flipH={flipH} flipV={flipV} ratio={ratio} rect={rectFor(file_2, source_1)} onRect={rect => setFrame({
        key: keyFor(file_2),
        rect
      })} />;
    };
    $[19] = flipH;
    $[20] = flipV;
    $[21] = keyFor;
    $[22] = ratio;
    $[23] = rectFor;
    $[24] = rotate;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== custom.h || $[27] !== custom.w || $[28] !== flipH || $[29] !== flipV || $[30] !== ratioId || $[31] !== rectFor || $[32] !== rotate) {
    t7 = t8 => {
      const {
        file: file_3,
        source: source_2
      } = t8;
      const crop_0 = file_3 && source_2 ? rotatedSize(toSourceCrop(rectFor(file_3, source_2), source_2, rotate, flipH, flipV), rotate) : null;
      return <><div className="flex flex-col gap-2"><span className="text-sm font-semibold" id="ratio-label">Shape</span><div role="group" aria-labelledby="ratio-label" className="flex flex-wrap gap-2">{RATIOS.map(r_0 => <button key={r_0.id} type="button" aria-pressed={ratioId === r_0.id} onClick={() => setRatioId(r_0.id)} className={cn(chipClass, "min-w-14", ratioId === r_0.id ? "border-brand bg-brand-tint font-semibold text-brand-strong" : "border-line-strong bg-card font-medium hover:bg-canvas")}>{r_0.label}</button>)}</div>{ratioId === "custom" && <div className="flex items-center gap-2 text-sm"><input className={cn(inputClass, "min-h-11 w-20")} inputMode="decimal" aria-label="Ratio width" value={custom.w} onChange={e => setCustom(c => ({
              ...c,
              w: e.target.value
            }))} />:<input className={cn(inputClass, "min-h-11 w-20")} inputMode="decimal" aria-label="Ratio height" value={custom.h} onChange={e_0 => setCustom(c_0 => ({
              ...c_0,
              h: e_0.target.value
            }))} /></div>}</div><div className="flex flex-col gap-2"><span className="text-sm font-semibold">Rotate and flip</span><div className="flex gap-2"><button type="button" className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg border-[1.5px] border-line-strong bg-card px-3 text-sm font-medium hover:bg-canvas aria-pressed:border-brand aria-pressed:bg-brand-tint" onClick={() => setRotate(_temp4)}><RotateCcw className="size-4" aria-hidden="true" />Left</button><button type="button" className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg border-[1.5px] border-line-strong bg-card px-3 text-sm font-medium hover:bg-canvas aria-pressed:border-brand aria-pressed:bg-brand-tint" onClick={() => setRotate(_temp5)}><RotateCw className="size-4" aria-hidden="true" />Right</button></div><div className="flex gap-2"><button type="button" aria-pressed={flipH} className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg border-[1.5px] border-line-strong bg-card px-3 text-sm font-medium hover:bg-canvas aria-pressed:border-brand aria-pressed:bg-brand-tint" onClick={() => flip("h")}><FlipVertical2 className="size-4" aria-hidden="true" />Flip sideways</button><button type="button" aria-pressed={flipV} className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg border-[1.5px] border-line-strong bg-card px-3 text-sm font-medium hover:bg-canvas aria-pressed:border-brand aria-pressed:bg-brand-tint" onClick={() => flip("v")}><FlipHorizontal2 className="size-4" aria-hidden="true" />Flip upside down</button></div></div><div className="flex items-center justify-between gap-3 rounded-lg bg-canvas p-3.5 text-sm"><span>Crop size{" "}<strong data-testid="crop-size">{crop_0 ? `${crop_0.width} × ${crop_0.height} px` : "\u2026"}</strong></span><button type="button" onClick={() => {
            setFrame(null);
            setRotate(0);
            setFlipH(false);
            setFlipV(false);
            setRatioId("free");
          }} className="min-h-11 rounded-lg px-3 font-semibold underline underline-offset-4 hover:bg-sand">Reset</button></div></>;
    };
    $[26] = custom.h;
    $[27] = custom.w;
    $[28] = flipH;
    $[29] = flipV;
    $[30] = ratioId;
    $[31] = rectFor;
    $[32] = rotate;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== t5 || $[35] !== t6 || $[36] !== t7) {
    t8 = <ImageTool tool={tool} zipName="images-cropped.zip" showResultOnly={true} actionLabel={_temp3} busyVerb="Cropping" plan={t5} editor={t6} settings={t7} />;
    $[34] = t5;
    $[35] = t6;
    $[36] = t7;
    $[37] = t8;
  } else {
    t8 = $[37];
  }
  return t8;
}
function _temp5(r_2) {
  return rotateBy(r_2, 1);
}
function _temp4(r_1) {
  return rotateBy(r_1, -1);
}
function _temp3() {
  return "Crop image";
}
function _temp2(v_0) {
  return !v_0;
}
function _temp(v) {
  return !v;
}